import { useEffect, useRef, useState } from 'react';
import { GroupProps } from '../Admin/types';

type UseTemplateSimilarityOptions = {
  templateId?: string | null;
};

const deepClone = <T,>(value: T): T => JSON.parse(JSON.stringify(value));

const areGroupsDifferent = (
  currentGroups: GroupProps[],
  initialGroups: GroupProps[]
): boolean => {
  if (!initialGroups || initialGroups.length === 0) {
    // If no initial groups, check if current groups have any meaningful content
    return currentGroups.some(
      (group) =>
        (group.headerText?.trim() ?? '') !== '' ||
        group.fields.some(
          (field) =>
            (field.headerText?.trim() ?? '') !== '' ||
            // Treat empty string cast for type-less initial fields
            (field.type as unknown as string) !== '' ||
            (field.selectOptions && field.selectOptions.length > 0)
        )
    );
  }

  // Deep comparison of groups
  if (currentGroups.length !== initialGroups.length) return true;

  return currentGroups.some((currentGroup, groupIndex) => {
    const initialGroup = initialGroups[groupIndex];
    if (!initialGroup) return true;

    if ((currentGroup.headerText ?? '') !== (initialGroup.headerText ?? ''))
      return true;

    if (currentGroup.fields.length !== initialGroup.fields.length) return true;

    return currentGroup.fields.some((currentField, fieldIndex) => {
      const initialField = initialGroup.fields[fieldIndex];
      if (!initialField) return true;

      return (
        (currentField.headerText ?? '') !== (initialField.headerText ?? '') ||
        currentField.type !== initialField.type ||
        currentField.isSecure !== initialField.isSecure ||
        (currentField.tooltip ?? '') !== (initialField.tooltip ?? '') ||
        JSON.stringify(currentField.selectOptions || []) !==
          JSON.stringify(initialField.selectOptions || [])
      );
    });
  });
};

export const useTemplateSimilarity = (
  groups: GroupProps[],
  options?: UseTemplateSimilarityOptions
) => {
  const { templateId } = options || {};
  const initialGroupsRef = useRef<GroupProps[] | null>(groups);
  const [hasChanges, setHasChanges] = useState(false);

  // Update initial groups when they are first loaded
  useEffect(() => {
    if (groups && groups.length > 0 && !initialGroupsRef.current) {
      initialGroupsRef.current = deepClone(groups);
    }
  }, [groups]);

  // Reset initial groups when template changes
  useEffect(() => {
    if (templateId) {
      initialGroupsRef.current = null;
    }
  }, [templateId]);

  // Check for changes whenever groups change
  useEffect(() => {
    if (initialGroupsRef.current) {
      setHasChanges(areGroupsDifferent(groups, initialGroupsRef.current));
    }
  }, [groups]);

  const resetBaseline = () => {
    initialGroupsRef.current = deepClone(groups);
    setHasChanges(false);
  };

  return { hasChanges, resetBaseline };
};

export type { GroupProps };
